import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import _ from 'lodash';
import {
  Box,
  Button,
  Backdrop,
  Divider,
  Fade,
  Grid,
  IconButton,
  Modal,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Loading from 'components/Loading';
import { CustomerForm } from 'components/Forms';
import ExpensesApplyForm from 'components/Forms/ExpensesApplyForm';
import FileUpload from 'components/FileUpload/FileUpload';
import * as actions from '../../redux/actions';
import api from '../../utils/functions/api';
import { showAlert, confirmAsync } from '../../utils/functions/globalDialog';

export default function EditExpensesModal({ open, expenseId, onClose, onSaved }) {
  const dispatch = useDispatch();
  const expenses = useSelector((state) => state.expenses);
  const projects = useSelector((state) => state.project);
  const budget = useSelector((state) => state.budget);
  const customer = useSelector((state) => state.customer);
  const customerType = useSelector((state) => state.customerType);

  const { control, formState: { errors }, handleSubmit, reset, setValue, watch } = useForm();
  const {
    control: customerControl,
    formState: { errors: customerErrors },
    handleSubmit: handleCustomerSubmit,
    reset: resetCustomer,
  } = useForm();

  const [date, setDate] = useState(null);
  const [projectSelect, setProjectSelect] = useState('');
  const [budgetSelect, setBudgetSelect] = useState('');
  const [bank, setBank] = useState();
  const [openCustomerModal, setOpenCustomerModal] = useState(false);
  const [customerSelect, setCustomerSelect] = useState();
  const [addedImage, setAddedImage] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const eachBudget = _.find(budget?.rows, { _id: budgetSelect?._id });

  useEffect(() => {
    if (open && expenseId) {
      dispatch(actions.expensesGet(expenseId));
      dispatch(actions.projectAll({ lite: true }));
      dispatch(actions.customerAll({}));
      dispatch(actions.customerTypeAll({}));
    }
  }, [open, expenseId]);

  useEffect(() => {
    if (!open) return;
    setValue('name', expenses?.name);
    setValue('price', expenses?.price);
    setValue('code', expenses?.code);
    setValue('withholding_tax', expenses?.withholding_tax);
    setValue('withholding_tax_rate', expenses?.withholding_tax_rate || 3);
    setValue('includes_vat', expenses?.includes_vat);
    setValue('vat_amount', expenses?.vat_amount);
    setValue(
      'base_amount',
      expenses?.base_amount ??
        (expenses?.includes_vat
          ? expenses?.price - (expenses?.vat_amount || 0)
          : (expenses?.net_price ?? expenses?.price)),
    );
    setValue(
      'net_price',
      expenses?.net_price ?? expenses?.price - (expenses?.withholding_tax || 0),
    );
    setValue('payee.name', expenses?.payee?.name);
    setValue('payee.account_number', expenses?.payee?.account_number);
    setValue('payee.bank', expenses?.payee?.bank);
    setValue('description', expenses?.description);
    setValue('expenses_type', expenses?.expenses_type || 'REFUND');
    setDate(expenses?.date);
    setBank(expenses?.payee?.bank);
    setCustomerSelect(expenses?.customer);
    setAddedImage([]);
  }, [expenses]);

  useEffect(() => {
    if (!open || !expenses || !projects?.rows) return;
    const projectObj = projects.rows.find((p) => p._id === expenses?.project?._id);
    if (projectObj) {
      setValue('project', projectObj);
      setProjectSelect(projectObj);
    } else {
      setValue('project', null);
      setProjectSelect(null);
    }
    // Budget will be set in the useEffect below after budgetAll loads
    setValue('customer', expenses?.customer?._id);
  }, [expenses, projects]);

  // Set budget after budgetAll loads (projectObj.budget is unavailable in lite mode)
  useEffect(() => {
    if (!open || !budget?.rows?.length || !expenses?.budget?._id) return;
    const budgetObj = budget.rows.find((b) => b._id === expenses.budget._id);
    if (budgetObj) {
      setValue('budget', budgetObj);
      setBudgetSelect(budgetObj);
    }
  }, [budget?.rows]);

  useEffect(() => {
    dispatch(actions.budgetReset());
    if (projectSelect?._id) {
      dispatch(actions.budgetAll({ project_id: projectSelect._id }));
    }
  }, [projectSelect]);

  const handleClose = () => {
    reset();
    setProjectSelect('');
    setBudgetSelect('');
    setDate(null);
    setBank(undefined);
    setCustomerSelect(undefined);
    setAddedImage([]);
    onClose();
  };

  const onSubmit = async (data) => {
    const confirm = await confirmAsync('บันทึกข้อมูล');
    if (confirm) {
      const dataSubmit = {
        ...data,
        bank,
        date: dayjs(date).endOf('day').toISOString(),
        budgetSelect: eachBudget,
        projectSelect,
        note: true,
        applie: expenses?.employee,
        customer: customerSelect,
        status: 'PENDING',
      };
      setIsSubmitting(true);
      try {
        // อัปโหลดรูปที่เพิ่มใหม่ทีละไฟล์ (multipart) แล้ว $push id เข้า bill_images แยก
        // จาก dataSubmit — แทนการยัด images: addedImage (base64 เต็มไฟล์) รวมเข้า PUT
        // เดียวกับฟิลด์อื่นของฟอร์ม กัน 413 Payload Too Large ตอนแนบรูปหลายรูป/ไฟล์ใหญ่
        if (addedImage.length > 0) {
          const uploadedImageIds = await Promise.all(
            addedImage.map(async (img) => {
              const formData = new FormData();
              formData.append('file', img.file, img.name);
              const { data: uploaded } = await api.post(
                `${process.env.REACT_APP_API_URL}/expenses/upload-image`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } },
              );
              return uploaded.id;
            }),
          );
          await dispatch(actions.expensesAddImages(expenseId, uploadedImageIds));
        }
        await dispatch(actions.expensesPut(expenseId, dataSubmit));
        await dispatch(actions.expensesAll({}));
        handleClose();
        onSaved?.();
      } catch (error) {
        showAlert(`บันทึกไม่สำเร็จ: ${error?.message || error}`, { status: error?.response?.status });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleRemoveExistingImage = async (imageId) => {
    const confirm = await confirmAsync('ลบรูปนี้?');
    if (confirm) {
      await dispatch(actions.expensesRemoveImage(expenseId, imageId));
      await dispatch(actions.expensesGet(expenseId));
    }
  };

  const onSubmitCustomer = async (data) => {
    try {
      data.bank = {
        bank_name: bank,
        account_name: data?.bank?.account_name,
        account_number: data?.bank?.account_number,
      };
      await dispatch(actions.customerCreate(data));
      await dispatch(actions.customerAll({}));
      setOpenCustomerModal(false);
      resetCustomer();
    } catch (error) {}
  };

  const isPdfUrl = (url) => url && url.toLowerCase().endsWith('.pdf');

  const isLoading = expenses?.isLoading || !projects?.isCompleted;

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 300 }}
      >
        <Fade in={open}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '95%', sm: '85%', md: 750 },
              maxHeight: '90vh',
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              p: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                p: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EditIcon color="warning" />
                <Typography variant="h6" fontWeight="bold">
                  แก้ไขรายการเบิกจ่าย
                </Typography>
              </Box>
              <IconButton size="small" onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Body */}
            {isLoading ? (
              <Box sx={{ p: 4 }}>
                <Loading isLoading />
              </Box>
            ) : (
              <Box
                component="form"
                onSubmit={handleSubmit(onSubmit)}
                sx={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column' }}
              >
                <Box sx={{ flex: 1 }}>
                  <ExpensesApplyForm
                    expenses={expenses}
                    control={control}
                    Controller={Controller}
                    date={date}
                    setDate={setDate}
                    bank={bank}
                    setBank={setBank}
                    projects={projects}
                    setProjectSelect={setProjectSelect}
                    projectSelect={projectSelect}
                    budgets={budget}
                    budgetSelect={budgetSelect}
                    setBudgetSelect={setBudgetSelect}
                    setValue={setValue}
                    customer={customer}
                    customerSelect={customerSelect}
                    onAddCustomer={() => setOpenCustomerModal(true)}
                    setCustomerSelect={setCustomerSelect}
                    watch={watch}
                  />

                  {/* Bill Images Section */}
                  <Box sx={{ px: 3, pb: 2 }}>
                    <Divider sx={{ mb: 2 }} />
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 1.5, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}
                    >
                      📎 รูปบิล / PDF
                    </Typography>

                    {/* Existing images */}
                    {(expenses?.bill_images?.length > 0 || expenses?.bill_image) && (
                      <Grid container spacing={1} sx={{ mb: 2 }}>
                        {expenses?.bill_image &&
                          (!expenses?.bill_images || expenses?.bill_images?.length === 0) && (
                            <Grid item xs={6} sm={4}>
                              <Box
                                sx={{
                                  position: 'relative',
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  overflow: 'hidden',
                                  cursor: 'pointer',
                                }}
                                onClick={() => window.open(expenses.bill_image.url, '_blank')}
                              >
                                <img
                                  src={expenses.bill_image.url}
                                  alt="Bill"
                                  style={{ width: '100%', height: 120, objectFit: 'cover' }}
                                />
                              </Box>
                            </Grid>
                          )}
                        {expenses?.bill_images?.map((img, index) => (
                          <Grid item xs={6} sm={4} key={img._id || index}>
                            <Box
                              sx={{
                                position: 'relative',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                overflow: 'hidden',
                              }}
                            >
                              {isPdfUrl(img.url) ? (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: 120,
                                    cursor: 'pointer',
                                    bgcolor: 'grey.50',
                                    '&:hover': { bgcolor: 'grey.100' },
                                  }}
                                  onClick={() => window.open(img.url, '_blank')}
                                >
                                  <PictureAsPdfIcon sx={{ fontSize: 40, color: 'error.main' }} />
                                  <Typography variant="caption" sx={{ mt: 0.5 }}>เปิด PDF</Typography>
                                  <OpenInNewIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                                </Box>
                              ) : (
                                <Box
                                  sx={{ cursor: 'pointer' }}
                                  onClick={() => window.open(img.url, '_blank')}
                                >
                                  <img
                                    src={img.url}
                                    alt={`Bill ${index + 1}`}
                                    style={{ width: '100%', height: 120, objectFit: 'cover' }}
                                  />
                                </Box>
                              )}
                              <IconButton
                                size="small"
                                color="error"
                                sx={{
                                  position: 'absolute',
                                  top: 4,
                                  right: 4,
                                  bgcolor: 'rgba(255,255,255,0.85)',
                                  '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
                                }}
                                onClick={() => handleRemoveExistingImage(img._id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    )}

                    {/* Upload new */}
                    <Box
                      sx={{
                        border: '2px dashed',
                        borderColor: 'divider',
                        borderRadius: 2,
                        p: 2,
                        bgcolor: 'action.hover',
                        textAlign: 'center',
                      }}
                    >
                      <FileUpload
                        maxNumber={10}
                        files={addedImage}
                        setFiles={setAddedImage}
                        title="อัปโหลดรูปบิล / PDF"
                        accept="image/*,.pdf"
                      />
                      {addedImage.length === 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          รองรับสูงสุด 10 ไฟล์ — รูปใหม่จะบันทึกพร้อมกับฟอร์ม
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* Footer */}
                <Box
                  sx={{
                    p: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1,
                  }}
                >
                  <Button variant="outlined" startIcon={<CloseIcon />} onClick={handleClose} disabled={isSubmitting}>
                    ยกเลิก
                  </Button>
                  <Button variant="contained" type="submit" startIcon={<CheckCircleIcon />} disabled={isSubmitting}>
                    {isSubmitting ? 'กำลังบันทึก...' : 'บันทึก'}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Fade>
      </Modal>

      {/* Add Customer sub-modal */}
      <Modal
        open={openCustomerModal}
        onClose={() => { setOpenCustomerModal(false); resetCustomer(); }}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 300 }}
      >
        <Fade in={openCustomerModal}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '95%', sm: '80%', md: 700 },
              maxHeight: '90vh',
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 24,
              p: 0,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                p: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonIcon sx={{ mr: 1 }} />
                <Typography variant="h6">เพิ่มลูกค้าใหม่</Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => { setOpenCustomerModal(false); resetCustomer(); }}
                sx={{ color: 'white' }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Box sx={{ p: 3, maxHeight: 'calc(90vh - 64px)', overflowY: 'auto' }}>
              <form onSubmit={handleCustomerSubmit(onSubmitCustomer)}>
                <CustomerForm
                  Controller={Controller}
                  control={customerControl}
                  errors={customerErrors}
                  customerType={customerType}
                  bank={bank}
                  setBank={setBank}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={() => { setOpenCustomerModal(false); resetCustomer(); }}
                    startIcon={<CloseIcon />}
                  >
                    ยกเลิก
                  </Button>
                  <Button variant="contained" type="submit" startIcon={<CheckCircleIcon />}>
                    บันทึกข้อมูล
                  </Button>
                </Box>
              </form>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </>
  );
}

EditExpensesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  expenseId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
};
